import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import BaseFormContainer from '../../../components/Form/FormContainer'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    makeStyles,
    mergeClasses,
    shorthands,
    Spinner,
    tokens,
} from '@fluentui/react-components'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import { DocketProceedingDto } from './DocketProceedingForm'
import { FieldProps } from '@fluentui/react-field'
import { DocketProceedingProps } from '../../../pages/File/Registration/DocketProceedingOverview'
import MasterDataPanel from './MasterDataPanel'
import DocketEntryList from './DocketEntryList'
import NavigationFormButtons from '../NavigationFormButtons'

export type DocketProceedingPanelProps = DocketProceedingProps & {
    orientation: FieldProps['orientation']
    styles: Record<'container' | 'input' | 'field' | 'formRow' | 'alert', string>
}

const useStyles = makeStyles({
    container: {
        display: 'grid',
        gridTemplateRows: 'auto min-content',
        ...shorthands.gap(tokens.spacingVerticalL),
    },
    accordionPanel: shorthands.margin(0),
    accordionHeader: {
        '> button': shorthands.padding(0),
    },
    sidePanel: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalL),
        ...shorthands.margin(0, '-8px'),
    },
    sidePanelLarge: shorthands.margin(0, '-16px'),
    formRow: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    input: { minWidth: 0, width: '100%', boxSizing: 'border-box', '> input': { textAlign: 'right', width: '100%' } },
    field: {
        alignItems: 'center',
        '> label': {
            ...shorthands.overflow('hidden'),
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
    },
    alert: {
        ...shorthands.margin(0, tokens.spacingHorizontalS),
    },
})

export default function FormContainer(props: DocketProceedingProps): ReactElement {
    const styles = useStyles()
    const { dirty, isSubmitting } = useFormikContext<DocketProceedingDto>()
    const { isViewport, viewport } = useViewport()
    const orientation = useMemo(() => (isViewport('>=', Viewport.Large) && isViewport('!=', Viewport.XL) ? 'horizontal' : 'vertical'), [viewport])

    useEffect(() => {
        // Keep track if the form is dirty, so we can warn the user if they try to navigate out.
        props.setSelected({ ...props.selected, isDirty: dirty })
    }, [dirty])

    if (isSubmitting) {
        return <Spinner label={'Bezig met opslaan...'} labelPosition={'below'} />
    }

    return (
        <BaseFormContainer
            sidePanel={
                <div className={mergeClasses(styles.sidePanel, isViewport('>=', Viewport.Large) && styles.sidePanelLarge)}>
                    <DocketEntryList orientation={orientation} styles={styles} {...props} />
                </div>
            }
        >
            <div className={styles.container}>
                <Accordion multiple collapsible defaultOpenItems={['base']}>
                    <AccordionItem value="base">
                        <AccordionHeader className={styles.accordionHeader}>Algemeen</AccordionHeader>
                        <AccordionPanel className={styles.accordionPanel}>
                            <MasterDataPanel orientation={orientation} styles={styles} {...props} />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
                <NavigationFormButtons {...props} deleteUrl={() => `/registration/docket-proceeding/${props.selected.id}`} />
            </div>
        </BaseFormContainer>
    )
}
